import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HeartPulse, RefreshCcw, Bell, CheckCircle2, TrendingDown, TrendingUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * HumeAlertsPanel — coach-facing panel that lists recovery-under-load
 * alerts computed from Hume × workload correlation. Includes a
 * "Scan roster" button that triggers ``/api/hume/coach/alerts/scan`` on
 * demand and fires a Resend email digest for any new findings.
 */
export default function HumeAlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/hume/coach/alerts", { params: { unread_only: false, limit: 20 } });
      setAlerts(data.alerts || []);
    } catch { /* silent — panel just stays empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/hume/coach/alerts/scan");
      if ((data.new_alerts || []).length === 0) {
        toast.success("Roster scan complete — no new recovery flags.");
      } else {
        toast.warning(`${data.new_alerts.length} new recovery alert${data.new_alerts.length !== 1 ? "s" : ""}`, {
          description: "Digest emailed to you — panel updated below.",
        });
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setScanning(false); }
  };

  const markRead = async (alertId) => {
    try {
      await api.post(`/hume/coach/alerts/${alertId}/read`);
      setAlerts((prev) => prev.map((a) => a.alert_id === alertId ? { ...a, read: true } : a));
    } catch { /* silent */ }
  };

  const unread = alerts.filter((a) => !a.read);

  return (
    <div className="ink-card rounded-xl p-5" data-testid="hume-alerts-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ backgroundColor: "rgba(217,4,41,0.15)", color: "#F43F5E" }}>
            <HeartPulse className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] font-bold text-white/45 uppercase">Hume × Workload</div>
            <div className="font-display font-black text-white text-lg">Recovery alerts</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unread.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-red-500/15 border border-red-500/40 text-red-300 text-[10px] font-bold uppercase tracking-wider"
                  data-testid="hume-alerts-unread-count">
              <Bell className="h-3 w-3" /> {unread.length} new
            </span>
          )}
          <Button
            size="sm"
            onClick={scan}
            disabled={scanning}
            className="text-white font-semibold text-xs border border-white/15 hover:bg-white/10 bg-transparent"
            data-testid="hume-alerts-scan-button"
          >
            {scanning
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Scanning…</>
              : <><RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Scan roster</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 text-white/50 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : alerts.length === 0 ? (
        <div className="mt-6 flex items-center gap-3 rounded-md border border-white/10 bg-white/[0.02] p-4"
             data-testid="hume-alerts-empty">
          <CheckCircle2 className="h-5 w-5 text-emerald-300" />
          <div className="text-sm text-white/70">
            No open alerts. Run <span className="text-white font-semibold">Scan roster</span> after today's Hume sync
            to check recovery vs. workload divergence.
          </div>
        </div>
      ) : (
        <ul className="mt-4 space-y-2" data-testid="hume-alerts-list">
          {alerts.map((a) => (
            <li key={a.alert_id}
                className={`rounded-md border p-3 flex items-start justify-between gap-4 ${a.read ? "border-white/8 bg-white/[0.015]" : "border-red-500/30 bg-red-500/5"}`}
                data-testid={`hume-alert-${a.alert_id}`}>
              <div className="flex items-start gap-3">
                <TrendingDown className="h-4 w-4 mt-1 text-red-400 shrink-0" />
                <div>
                  <div className="text-sm text-white font-semibold">
                    <Link to={`/dashboard/player/${a.athlete_user_id}`} className="hover:text-fuchsia-300">
                      {a.athlete_name}
                    </Link>
                  </div>
                  <div className="text-[11px] text-white/60 mt-0.5 flex flex-wrap items-center gap-3">
                    <span className="font-mono-num">{a.date}</span>
                    <span className="text-red-300"><TrendingDown className="inline h-3 w-3 mr-1" />Recovery Δ {a.recovery_delta}</span>
                    <span className="text-white/75"><TrendingUp className="inline h-3 w-3 mr-1" />Workload +{a.workload_delta}</span>
                    {a.hrv_ms != null && <span>HRV {a.hrv_ms} ms</span>}
                  </div>
                </div>
              </div>
              {!a.read && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => markRead(a.alert_id)}
                  className="text-xs text-white/70 hover:text-white shrink-0"
                  data-testid={`hume-alert-mark-read-${a.alert_id}`}
                >
                  Mark read
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
